import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert, Swords, Flame, Activity, ArrowRight,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import HeatmapStadium from "@/components/crowd/HeatmapStadium";

const SEV_COLOR = {
  critical: "hsl(var(--destructive))",
  high:     "hsl(var(--warning))",
  medium:   "hsl(var(--primary))",
  low:      "hsl(var(--muted-foreground))",
};

const CATEGORY_META = {
  intrusion:  { icon: ShieldAlert, accent: "hsl(var(--destructive))", description: "Perimeter breaches · fence climbs · unauthorized access" },
  fight:      { icon: Swords,      accent: "hsl(var(--warning))",     description: "Physical altercations and aggressive behavior in crowds" },
  fire_smoke: { icon: Flame,       accent: "hsl(var(--destructive))", description: "Smoke signatures and active fire detection" },
  congestion: { icon: Activity,    accent: "hsl(var(--primary))",     description: "Crowd surge · stampede risk · density spikes" },
};

const titleCase = (s) =>
  String(s || "").split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const relTime = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const TrendSpark = ({ data, color }) => {
  const series = (data || []).map((d, i) => ({ i, v: d.count }));
  return (
    <div className="h-12">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`vi-spark-${color.replace(/[^a-z0-9]/gi, "")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.45} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#vi-spark-${color.replace(/[^a-z0-9]/gi, "")})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const CategoryCard = ({ category, onOpenIncident }) => {
  const meta = CATEGORY_META[category.key];
  const Icon = meta.icon;
  const navigate = useNavigate();

  return (
    <div
      className="panel p-3 flex flex-col gap-2 border-l-2"
      style={{ borderLeftColor: meta.accent }}
      data-testid={`vi-card-${category.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-7 h-7 flex items-center justify-center shrink-0"
            style={{ background: `${meta.accent}` }}
          >
            <Icon className="w-4 h-4 text-[hsl(var(--background))]" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-xs leading-tight truncate" data-testid={`vi-card-${category.key}-title`}>
              {category.label}
            </div>
            <div className="micro-label text-[9px] mt-0.5 truncate">{meta.description}</div>
          </div>
        </div>
        <button
          onClick={() => navigate("/incidents")}
          className="text-[10px] text-[hsl(var(--primary))] hover:underline shrink-0"
          data-testid={`vi-card-${category.key}-open`}
        >
          Incidents →
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1 mt-1">
        <div>
          <div className="micro-label text-[9px]">Total</div>
          <div className="font-display font-black text-lg leading-none" data-testid={`vi-card-${category.key}-total`}>
            {category.total}
          </div>
        </div>
        <div>
          <div className="micro-label text-[9px]">Last 24h</div>
          <div className="font-display font-black text-lg leading-none" style={{ color: meta.accent }}>
            {category.last_24h}
          </div>
        </div>
        <div className="min-w-0">
          <div className="micro-label text-[9px]">Hotspot</div>
          <div className="font-mono text-[10px] truncate" title={category.top_zone}>
            {category.top_zone}
            {category.top_zone_count > 0 && (
              <span className="text-muted-foreground"> · {category.top_zone_count}</span>
            )}
          </div>
        </div>
      </div>

      <TrendSpark data={category.trend} color={meta.accent} />

      {/* Severity micro-bar */}
      <div className="flex items-center gap-2 text-[10px] font-mono">
        {category.severity.map((s) => (
          s.count > 0 && (
            <span key={s.severity} className="inline-flex items-center gap-1" style={{ color: SEV_COLOR[s.severity] }}>
              <span className="w-1.5 h-1.5" style={{ background: SEV_COLOR[s.severity] }} />
              {titleCase(s.severity)} {s.count}
            </span>
          )
        ))}
      </div>

      {/* Live feed */}
      <div className="mt-1 border-t border-border pt-2">
        <div className="micro-label text-[9px] mb-1">Recent Events</div>
        {category.feed.length === 0 ? (
          <div className="text-[10px] text-muted-foreground py-2">No events in this category.</div>
        ) : (
          <ol className="space-y-1" data-testid={`vi-card-${category.key}-feed`}>
            {category.feed.slice(0, 4).map((e) => (
              <li
                key={e.id}
                className="flex items-start gap-2 text-[10px] py-1 px-1.5 hover:bg-[hsl(var(--surface-elevated))] cursor-pointer"
                onClick={() => onOpenIncident?.(e.id)}
                data-testid={`vi-card-${category.key}-feed-${e.id}`}
              >
                <span
                  className="mt-1 w-1.5 h-1.5 shrink-0 rounded-full"
                  style={{ background: SEV_COLOR[e.severity] }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-foreground truncate">{e.title}</div>
                  <div className="font-mono text-[9px] text-muted-foreground flex items-center gap-1 flex-wrap">
                    <span>{e.zone_name}</span>
                    <span>·</span>
                    <span>{relTime(e.ts)}</span>
                    {e.camera_id && <><span>·</span><span>{e.camera_id}</span></>}
                  </div>
                </div>
                <span
                  className="px-1 text-[9px] font-bold tracking-wide border self-start mt-0.5"
                  style={{ color: SEV_COLOR[e.severity], borderColor: SEV_COLOR[e.severity] }}
                >
                  {titleCase(e.severity)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

export const VisualImpactTab = ({ visualImpact, heatmap, onOpenIncident }) => {
  const v = visualImpact || {};
  const cats = ["intrusion", "fight", "fire_smoke", "congestion"]
    .map((k) => v[k])
    .filter(Boolean);

  return (
    <div className="p-3 sm:p-4 space-y-3" data-testid="vi-tab">
      {/* Top: crowd heatmap (centerpiece) */}
      <div className="panel p-2">
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="h-widget flex items-center gap-1">
            <Activity className="w-3 h-3" /> Crowd Density Heatmap
          </div>
          <span className="micro-label">Real-Time · Click Zone To Drill Down</span>
        </div>
        <div className="h-[360px] sm:h-[420px]">
          <HeatmapStadium zones={heatmap || []} />
        </div>
      </div>

      {/* 4 category cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="vi-category-grid">
        {cats.map((c) => (
          <CategoryCard key={c.key} category={c} onOpenIncident={onOpenIncident} />
        ))}
      </div>

      {/* Summary footer */}
      <div className="flex items-center justify-end gap-2 text-[10px] text-muted-foreground">
        <span>Detected across all 4 categories: <span className="font-mono text-foreground">{v._summary?.total_events ?? 0}</span></span>
      </div>
    </div>
  );
};

export default VisualImpactTab;
